import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { languageNames } from '../config/displayLanguages';

// Supported languages
export type Language = string;

export const USER_LANG_STORAGE_KEY = 'paircraft_user_lang_preference';

export { languageNames };

// RTL languages
const RTL_LANGUAGES = ['ar'];

export type SetLanguageOptions = { fromUser?: boolean };

interface LanguageContextType {
    language: string;
    setLanguage: (lang: string, options?: SetLanguageOptions) => void;
    /** t() is kept for backward compat but auto-translation handles everything */
    t: (key: string, fallback?: string) => string;
    translateText: (text: string) => string;
    preloadTranslations: (texts: string[]) => void;
    availableLanguages: string[];
    languageNames: Record<string, string>;
    isTranslating: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = (): LanguageContextType => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

/**
 * Read ?lang=xx from the current URL (no param → null)
 */
function getLanguageFromURL(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const lang = params.get('lang');
        if (lang && lang in languageNames) {
            return lang;
        }
    } catch {
        /* ignore */
    }
    return null;
}

function readStoredUserLanguage(): string | null {
    try {
        const v = localStorage.getItem(USER_LANG_STORAGE_KEY);
        if (v && v in languageNames) return v;
    } catch {
        /* ignore */
    }
    return null;
}

function getLanguageFromBrowser(): string | null {
    try {
        const lang = navigator.language;
        if (!lang) return null;
        const code = lang.split('-')[0].toLowerCase();
        if (code in languageNames && code !== 'en') return code;
    } catch {
        /* ignore */
    }
    return null;
}

function getInitialLanguage(): string {
    const urlLang = getLanguageFromURL();
    if (urlLang) return urlLang;
    const userPref = readStoredUserLanguage();
    if (userPref) return userPref;
    const browserLang = getLanguageFromBrowser();
    if (browserLang) return browserLang;
    return 'en';
}

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    const [language, setLanguageState] = useState<string>(getInitialLanguage);
    const [isTranslating] = useState(false);

    // Listen for URL changes (popstate) and re-read ?lang= when present
    useEffect(() => {
        const handleUrlChange = () => {
            const newLang = getLanguageFromURL();
            if (newLang) {
                setLanguageState((prev) => (prev !== newLang ? newLang : prev));
            }
        };

        const interval = setInterval(handleUrlChange, 500);

        window.addEventListener('popstate', handleUrlChange);
        return () => {
            clearInterval(interval);
            window.removeEventListener('popstate', handleUrlChange);
        };
    }, []);

    // Update document attributes when language changes
    useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';
    }, [language]);

    const setLanguage = useCallback((lang: string, options?: SetLanguageOptions) => {
        setLanguageState(lang);
        if (options?.fromUser) {
            try {
                localStorage.setItem(USER_LANG_STORAGE_KEY, lang);
            } catch {
                /* ignore */
            }
        }
        const url = new URL(window.location.href);
        if (lang === 'en') {
            url.searchParams.delete('lang');
        } else {
            url.searchParams.set('lang', lang);
        }
        window.history.replaceState({}, '', url.toString());
        document.documentElement.lang = lang;
        document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
    }, []);

    /**
     * t() — Returns the fallback directly. Google Website Translate rewrites visible text.
     * This is kept for backward compatibility with components that already use it.
     */
    const t = useCallback((_key: string, fallback?: string): string => {
        return fallback || _key;
    }, []);

    const translateText = useCallback((text: string): string => text, []);
    const preloadTranslations = useCallback((_texts: string[]) => {}, []);

    const availableLanguages = Object.keys(languageNames);

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage,
            t,
            translateText,
            preloadTranslations,
            availableLanguages,
            languageNames,
            isTranslating,
        }}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
